import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard, faChartLine, faLayerGroup, faReceipt, faXmark, faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./PaymentsAdminModal.module.css";
import ModalHeader from "./ModalHeader";
import PlanEditorTable from "./PlanEditorTable";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const SUB_STATUS_LABELS = {
  active: "Ativa", pending: "Pendente", canceled: "Cancelada", expired: "Expirada", past_due: "Atrasada",
};
const PAYMENT_STATUS_LABELS = {
  pending: "Aguardando", confirmed: "Confirmado", received: "Pago", overdue: "Vencido",
  refunded: "Estornado", failed: "Falhou",
};

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Erro inesperado.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(" - ");
  return JSON.stringify(detail);
}

function formatMoney(value) {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch (_) {
    return iso;
  }
}

function KpiCard({ label, value, sub }) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiVal}>{value}</div>
      <div className={styles.kpiLabel}>{label}</div>
      {sub && <div className={styles.kpiSub}>{sub}</div>}
    </div>
  );
}

export default function PaymentsAdminModal({ open, onClose }) {
  useBodyScrollLock(open);

  const [tab, setTab] = useState("summary"); // summary | plans | subscriptions | payments
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState(null);

  const [subscriptions, setSubscriptions] = useState([]);
  const [subsHasMore, setSubsHasMore] = useState(false);
  const [loadingMoreSubs, setLoadingMoreSubs] = useState(false);
  const [subStatusFilter, setSubStatusFilter] = useState("");
  const [cancelingSubId, setCancelingSubId] = useState(null);
  const [confirmCancelId, setConfirmCancelId] = useState(null);

  const [payments, setPayments] = useState([]);
  const [paymentsHasMore, setPaymentsHasMore] = useState(false);
  const [loadingMorePayments, setLoadingMorePayments] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");

  const loadSummary = useCallback(async () => {
    const { data } = await api.get("/payments/admin/summary");
    setSummary(data);
  }, []);

  const loadSubscriptions = useCallback(async (status) => {
    const res = await api.get("/payments/admin/subscriptions", { params: status ? { status } : {} });
    setSubscriptions(res.data);
    setSubsHasMore(res.headers?.["x-has-more"] === "true");
  }, []);

  const loadPayments = useCallback(async (status) => {
    const res = await api.get("/payments/admin/payments", { params: status ? { status } : {} });
    setPayments(res.data);
    setPaymentsHasMore(res.headers?.["x-has-more"] === "true");
  }, []);

  // "Carregar mais": busca a próxima página (offset = já carregado) e anexa.
  // Backend pagina em app/utils/pagination.py e sinaliza via X-Has-More.
  const loadMoreSubs = useCallback(async () => {
    if (loadingMoreSubs) return;
    setLoadingMoreSubs(true);
    try {
      const params = { offset: subscriptions.length };
      if (subStatusFilter) params.status = subStatusFilter;
      const res = await api.get("/payments/admin/subscriptions", { params });
      const novos = Array.isArray(res.data) ? res.data : [];
      if (novos.length) setSubscriptions((prev) => [...prev, ...novos]);
      setSubsHasMore(res.headers?.["x-has-more"] === "true");
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoadingMoreSubs(false);
    }
  }, [loadingMoreSubs, subscriptions.length, subStatusFilter]);

  const loadMorePayments = useCallback(async () => {
    if (loadingMorePayments) return;
    setLoadingMorePayments(true);
    try {
      const params = { offset: payments.length };
      if (paymentStatusFilter) params.status = paymentStatusFilter;
      const res = await api.get("/payments/admin/payments", { params });
      const novos = Array.isArray(res.data) ? res.data : [];
      if (novos.length) setPayments((prev) => [...prev, ...novos]);
      setPaymentsHasMore(res.headers?.["x-has-more"] === "true");
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoadingMorePayments(false);
    }
  }, [loadingMorePayments, payments.length, paymentStatusFilter]);

  const loadTab = useCallback(async (t) => {
    // "plans" carrega os próprios dados dentro de PlanEditorTable — nada a
    // fazer aqui além de não acionar loading/error compartilhado à toa.
    if (t === "plans") return;
    setLoading(true);
    setError("");
    try {
      if (t === "summary") await loadSummary();
      else if (t === "subscriptions") await loadSubscriptions(subStatusFilter);
      else if (t === "payments") await loadPayments(paymentStatusFilter);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [loadSummary, loadSubscriptions, loadPayments, subStatusFilter, paymentStatusFilter]);

  useEffect(() => {
    if (!open) return;
    loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  useEffect(() => {
    if (!open || tab !== "subscriptions") return;
    loadSubscriptions(subStatusFilter).catch(err => setError(extractError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStatusFilter]);

  useEffect(() => {
    if (!open || tab !== "payments") return;
    loadPayments(paymentStatusFilter).catch(err => setError(extractError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatusFilter]);

  useEffect(() => {
    if (!open) {
      setTab("summary");
      setError("");
      setConfirmCancelId(null);
    }
  }, [open]);

  async function handleCancelSubscription(subId) {
    setCancelingSubId(subId);
    setError("");
    try {
      await api.delete(`/payments/subscriptions/${subId}`);
      setConfirmCancelId(null);
      await loadSubscriptions(subStatusFilter);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setCancelingSubId(null);
    }
  }

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} />
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.icon}><FontAwesomeIcon icon={faCreditCard} /></span>
            <div>
              <h2 className={styles.title}>Gestão de pagamentos</h2>
              <p className={styles.sub}>Planos, assinaturas e pagamentos (Asaas)</p>
            </div>
          </div>
        </div>

        <div className={styles.tabs}>
          <button className={`${styles.tabBtn} ${tab === "summary" ? styles.tabBtnActive : ""}`} onClick={() => setTab("summary")}>
            <FontAwesomeIcon icon={faChartLine} /> Resumo
          </button>
          <button className={`${styles.tabBtn} ${tab === "plans" ? styles.tabBtnActive : ""}`} onClick={() => setTab("plans")}>
            <FontAwesomeIcon icon={faLayerGroup} /> Planos
          </button>
          <button className={`${styles.tabBtn} ${tab === "subscriptions" ? styles.tabBtnActive : ""}`} onClick={() => setTab("subscriptions")}>
            <FontAwesomeIcon icon={faReceipt} /> Assinaturas
          </button>
          <button className={`${styles.tabBtn} ${tab === "payments" ? styles.tabBtnActive : ""}`} onClick={() => setTab("payments")}>
            <FontAwesomeIcon icon={faCreditCard} /> Pagamentos
          </button>
        </div>

        <div className={styles.body}>
          {error && <div className={styles.error}>{error}</div>}

          {loading ? (
            <div className={styles.loadingMsg}>Carregando…</div>

          ) : tab === "summary" ? (
            <>
              <div className={styles.kpiGroup}>
                <h4 className={styles.kpiGroupTitle}>Plataforma</h4>
                <div className={styles.kpiGrid}>
                  <KpiCard label="Assinantes ativos" value={summary?.active_subscriptions ?? "-"} sub="alunos + professores" />
                  <KpiCard label="MRR" value={formatMoney(summary?.mrr)} sub="receita recorrente mensal" />
                  <KpiCard label="Pagamentos pendentes" value={summary?.pending_payments ?? "-"} />
                  <KpiCard label="Pagamentos vencidos" value={summary?.overdue_payments ?? "-"} />
                  <KpiCard label="Receita avulsa" value={formatMoney(summary?.one_off_paid_total)} sub={`${summary?.one_off_paid_count ?? 0} pagamento(s) pago(s)`} />
                </div>
              </div>

              <div className={styles.kpiGroup}>
                <h4 className={styles.kpiGroupTitle}>Professores</h4>
                <div className={styles.kpiGrid}>
                  <KpiCard label="Pagantes" value={summary?.professors_paying ?? "-"} sub="assinatura ativa" />
                  <KpiCard label="Plano Básico" value={summary?.professors_basic ?? "-"} sub="sem gestão de alunos" />
                  <KpiCard label="Plano Premium" value={summary?.professors_premium ?? "-"} sub="com gestão de alunos" />
                  <KpiCard label="Degustação parcial" value={summary?.professors_partial_trial ?? "-"} sub="não paga, gerencia alunos" />
                </div>
              </div>
            </>


          ) : tab === "plans" ? (
            <PlanEditorTable
              listUrl="/payments/admin/plans"
              createUrl="/payments/admin/plans"
              updateUrlPrefix="/payments/admin/plans"
              showAudienceField
            />

          ) : tab === "subscriptions" ? (
            <div className={styles.listSection}>
              <div className={styles.filterRow}>
                <select className={styles.inlineInput} value={subStatusFilter} onChange={e => setSubStatusFilter(e.target.value)}>
                  <option value="">Todos os status</option>
                  {Object.entries(SUB_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <button className={styles.iconBtn} onClick={() => loadSubscriptions(subStatusFilter)} title="Atualizar">
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>
              </div>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Usuário</th><th>Plano</th><th>Status</th><th>Próx. cobrança</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map(sub => (
                    <tr key={sub.id}>
                      <td>{sub.user_name}<br /><span className={styles.muted}>{sub.user_email}</span></td>
                      <td>{sub.plan_name}</td>
                      <td><span className={styles.statusPill}>{SUB_STATUS_LABELS[sub.status] || sub.status}</span></td>
                      <td>{formatDate(sub.current_period_end)}</td>
                      <td className={styles.actionsCell}>
                        {sub.status === "active" && (
                          confirmCancelId === sub.id ? (
                            <>
                              <button className={styles.btnDangerSmall} onClick={() => handleCancelSubscription(sub.id)} disabled={cancelingSubId === sub.id}>
                                {cancelingSubId === sub.id ? "Cancelando…" : "Confirmar"}
                              </button>
                              <button className={styles.iconBtn} onClick={() => setConfirmCancelId(null)}>
                                <FontAwesomeIcon icon={faXmark} />
                              </button>
                            </>
                          ) : (
                            <button className={styles.btnDangerSmall} onClick={() => setConfirmCancelId(sub.id)}>
                              Cancelar
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {subscriptions.length === 0 && (
                    <tr><td colSpan={5} className={styles.emptyMsg}>Nenhuma assinatura encontrada.</td></tr>
                  )}
                </tbody>
              </table>
              {subsHasMore && (
                <button className={styles.btnSecondarySmall} onClick={loadMoreSubs} disabled={loadingMoreSubs} style={{ marginTop: 12 }}>
                  {loadingMoreSubs ? "Carregando…" : "Carregar mais"}
                </button>
              )}
            </div>

          ) : (
            <div className={styles.listSection}>
              <div className={styles.filterRow}>
                <select className={styles.inlineInput} value={paymentStatusFilter} onChange={e => setPaymentStatusFilter(e.target.value)}>
                  <option value="">Todos os status</option>
                  {Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <button className={styles.iconBtn} onClick={() => loadPayments(paymentStatusFilter)} title="Atualizar">
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>
              </div>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Usuário</th><th>Método</th><th>Valor</th><th>Status</th><th>Tipo</th><th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id}>
                      <td>{p.user_name}<br /><span className={styles.muted}>{p.user_email}</span></td>
                      <td>{p.method}</td>
                      <td>{formatMoney(p.amount)}</td>
                      <td><span className={styles.statusPill}>{PAYMENT_STATUS_LABELS[p.status] || p.status}</span></td>
                      <td>{p.type}</td>
                      <td>{formatDate(p.created_at)}</td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr><td colSpan={6} className={styles.emptyMsg}>Nenhum pagamento encontrado.</td></tr>
                  )}
                </tbody>
              </table>
              {paymentsHasMore && (
                <button className={styles.btnSecondarySmall} onClick={loadMorePayments} disabled={loadingMorePayments} style={{ marginTop: 12 }}>
                  {loadingMorePayments ? "Carregando…" : "Carregar mais"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
